<script setup lang="ts">
  import { useUser } from '@/composables/useUser'
  import { GetExecutionStatusArgs } from '@/shared/Executions/models/GetExecutionStatusArgs'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { getDuration } from '@/utils'
  import { QuestionCircleOutlined } from '@ant-design/icons-vue'
  import { EllipsisVertical } from 'lucide-vue-next'
  import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import { Execution } from '@/shared/Executions/models/Execution'
  import ExecutionStatusBadge from './ExecutionStatusBadge.vue'
  import { ExecutionError } from '@/shared/Executions/models/ExecutionError'
  import { ExecutionApiService } from '../services/ExecutionApiService'
  import Stopwatch from '@/components/Stopwatch.vue'

  const TERMINAL_STATUSES: ExecutionStatusType[] = [
    ExecutionStatusType.Succeeded,
    ExecutionStatusType.Failed,
    ExecutionStatusType.Cancelled,
    ExecutionStatusType.Rejected,
    ExecutionStatusType.Throttled,
    ExecutionStatusType.Timeout
  ]

  interface IExecutionStatusComponentProps {
    execution?: Execution
    showRunTime: boolean
    showLeftSplitter?: boolean
    showRightSplitter?: boolean
  }

  const auth = useUser()

  const props = defineProps<IExecutionStatusComponentProps>()

  const emit = defineEmits<{
    (e: 'notifyExecutionStatusChanged', status: ExecutionStatusType, execution: Execution): void
    (e: 'notifyExecutionComplete', success: boolean, step: Execution): void
  }>()

  const executionApiService: ExecutionApiService = InjectionService.injectStrict(
    ExecutionApiService.injectableKey
  )

  const checkStatusIntervalId = ref<any>(null)
  const totalTimeStopWatch = ref<any>(null)
  const errors = ref<Array<ExecutionError>>([])
  const execution = ref<Execution | undefined>(props.execution)
  const executionStatusType = ref<ExecutionStatusType>(
    props.execution?.status ?? ExecutionStatusType.Unexecuted
  )
  const fullRuntime = ref<string | null>()

  watch(
    () => props.execution,
    (newValue) => {
      execution.value = newValue
      executionStatusType.value = newValue?.status ?? ExecutionStatusType.Unknown
    },
    { immediate: true }
  )

  const tryGetUpdatedStatus = async (executionId: string) => {
    const args = new GetExecutionStatusArgs({
      userId: auth.user.id as string,
      tenantId: auth.user.tenant_id as string,
      executionId
    })

    const response = await executionApiService.getExecutionStatus(args)
    execution.value = response?.data

    executionStatusType.value = execution.value.status as ExecutionStatusType
    totalTimeStopWatch.value?.start(execution?.value.startedUtc ?? '')
    emit(
      'notifyExecutionStatusChanged',
      execution?.value.status as ExecutionStatusType,
      execution.value as Execution
    )

    if (TERMINAL_STATUSES.includes(execution?.value.status as ExecutionStatusType)) {
      errors.value = execution?.value?.errors ?? []

      clearInterval(checkStatusIntervalId.value)
      totalTimeStopWatch.value?.stop()
      const startUtc = execution?.value?.startedUtc
      const endUtc = execution?.value?.completedUtc
      fullRuntime.value = getDuration(startUtc ?? null, endUtc ?? null)
      emit(
        'notifyExecutionComplete',
        execution?.value.status === ExecutionStatusType.Succeeded ? true : false,
        execution?.value
      )
    }
  }

  const startPollingStatus = () => {
    if (props?.execution?.id != null) {
      totalTimeStopWatch.value?.start(props?.execution?.startedUtc ?? '')
      checkStatusIntervalId.value = setInterval(async () => {
        await tryGetUpdatedStatus(props?.execution?.id!)
      }, 2000)
    }
  }

  onMounted(async () => {
    await auth.initialize()
    if (!TERMINAL_STATUSES.includes(executionStatusType.value)) {
      nextTick(() => {
        startPollingStatus()
      })
    }
  })

  onBeforeUnmount(() => {
    if (checkStatusIntervalId.value) {
      clearInterval(checkStatusIntervalId.value)
    }
  })

  defineExpose({
    startPollingStatus
  })
</script>

<template>
  <div class="ml-5 mr-5">
    <!-- Status Runtime -->
    <div
      class="flex items-center gap-3 space-x-1 text-xs text-muted-foreground sm:text-xs md:text-xs lg:text-sm"
    >
      <EllipsisVertical v-if="props.showLeftSplitter" />

      <!-- Status Badge -->
      <ExecutionStatusBadge :execution="execution" :errors="errors" />

      <div v-if="executionStatusType !== ExecutionStatusType.Succeeded && showRunTime">
        <a-tooltip>
          <template #title>
            This is an estimated runtime based on a counter running locally from the time of
            submission.
            <br />
          </template>

          <div class="flex items-center space-x-1">
            <span>Run Time</span>
            <QuestionCircleOutlined className="inline-block" />
          </div>
        </a-tooltip>

        <div class="flex justify-center font-bold text-gray-900 dark:text-gray-100">
          <Stopwatch ref="totalTimeStopWatch" />
        </div>
      </div>

      <EllipsisVertical v-if="props.showRightSplitter" />
    </div>
  </div>
</template>
